
import React from "react";
import { Investor } from "@/types/investor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Sparkles, Eye, CheckCircle } from "lucide-react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { getRelevanceBadgeVariant } from "@/utils/uiHelpers";
import { hasInvestorBeenEmailed } from "@/utils/emailUtils";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { hookDebugUtils } from "@/utils/ai/debugUtils";

interface InvestorCardItemProps {
  investor: Investor;
  isSelected: boolean;
  onSelectInvestor: (investor: Investor) => void;
  onEnrichInvestor?: (investor: Investor) => Promise<Investor>;
  isInvestorEnriching?: (id: string) => boolean;
  isInvestorProcessed: (investor: Investor) => boolean;
  onViewInvestor: (investor: Investor) => void;
}

const InvestorCardItem = ({
  investor,
  isSelected,
  onSelectInvestor,
  onEnrichInvestor,
  isInvestorEnriching,
  isInvestorProcessed,
  onViewInvestor
}: InvestorCardItemProps) => {
  const isEmailed = hasInvestorBeenEmailed(investor.id);
  const isCurrentlyEnriching = isInvestorEnriching ? isInvestorEnriching(investor.id) : false;
  const processed = isInvestorProcessed(investor);

  // Safe render helper to prevent rendering objects directly
  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    return hookDebugUtils.safeObjectDisplay(value) || "";
  };

  // Handle enrich with confirmation for re-enrichment to prevent accidental data loss
  const handleEnrichClick = async () => {
    if (!onEnrichInvestor) return;
    
    // If already processed, confirm to prevent accidental data loss
    if (processed && investor.enrichedWith) {
      const confirmReprocess = window.confirm(
        `This investor (${investor.firm}) has already been enriched with ${investor.enrichedWith}. Are you sure you want to re-enrich? This will overwrite existing data.`
      );
      
      if (!confirmReprocess) {
        console.log("Re-enrichment cancelled by user");
        return;
      }
    }
    
    // Proceed with enrichment
    await onEnrichInvestor(investor);
  };

  return (
    <Card 
      className={`h-full ${isSelected ? "border-primary" : ""}`}
      data-state={isSelected ? "selected" : undefined}
    >
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate max-w-[180px]" title={investor.firm}>
              {safeRender(investor.firm)}
            </h3>
            {isEmailed && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CheckCircle size={16} className="text-green-600" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Email has been sent</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {processed ? (
            <Badge variant={getRelevanceBadgeVariant(investor.relevanceScore)}>
              {investor.relevanceScore.toFixed(1)}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-gray-100 text-gray-500">
              Unprocessed
            </Badge>
          )}
        </div>
        
        <div className="text-sm text-gray-600 h-24 overflow-y-auto mb-2" title={investor.investmentFocus}>
          {safeRender(investor.investmentFocus) || "No investment focus available"}
        </div>
        
        {investor.portfolioCompanies && investor.portfolioCompanies.length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-gray-700">Portfolio:</p>
            <div className="text-xs text-gray-600 h-12 overflow-y-auto">
              {investor.portfolioCompanies.map((company, i) => 
                <span key={i}>{safeRender(company)}{i < investor.portfolioCompanies!.length - 1 ? ', ' : ''}</span>
              )}
            </div>
          </div>
        )}
        
        <div className="mt-2 text-xs text-gray-500">
          {investor.lastRelevanceUpdate ? (
            `Updated: ${new Date(investor.lastRelevanceUpdate).toLocaleDateString()}`
          ) : (
            "Not processed"
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-3 pt-0 gap-2 flex-wrap">
        <Button 
          variant="secondary" 
          size="sm"
          onClick={() => onSelectInvestor(investor)}
          className="flex-1 min-w-0"
        >
          <Mail className="mr-1 h-3 w-3" />
          Select
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewInvestor(investor)}
          className="flex-1 min-w-0"
        >
          <Eye className="mr-1 h-3 w-3" />
          View
        </Button>
        
        {onEnrichInvestor && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEnrichClick}
            disabled={isCurrentlyEnriching}
            className="w-full mt-2"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            {isCurrentlyEnriching 
              ? "Enriching..." 
              : (processed ? "Re-enrich" : "Enrich")}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default InvestorCardItem;
